/**
 * Quill 에디터 래퍼 — admin/comm 공용.
 *
 * 기능:
 *   - 툴바: font picker + H1~H6 heading + 기본 인라인 서식
 *   - 이미지: 툴바 버튼 / 붙여넣기 / 드롭 시 uploadUrl 로 업로드 후 드롭 위치에 URL 삽입 (data: base64 방지)
 *   - 동영상: 툴바 버튼 / 드래그앤드롭 / 붙여넣기 시 업로드 후 video embed 삽입 (옵션)
 *   - 미디어: 삽입된 이미지·비디오 embed 를 editor 안에서 드래그 이동
 *   - 테이블: quill-table-better 모듈로 표 삽입·행·열 추가·셀 병합 (옵션)
 *
 * 레이아웃에서 window.Quill (lib/quill/quill.js) + window.QuillTableBetter
 * (lib/quill-table-better/quill-table-better.js) 가 먼저 로드되어 있어야 한다.
 * 절대규칙 #2 — 모든 외부 코드는 본 래퍼만 import. Quill·QuillTableBetter 를 직접 만들지 말 것.
 */
import { Modal } from "../utils/Modal";
import { t } from "../utils/I18n";
import { richTextFontOptionsForQuill } from "./RichTextOptions";

interface QuillBetterTableModule {
    insertTable: (rows: number, cols: number) => void;
}

interface QuillStatic {
    new (el: HTMLElement, options?: QuillOptions): QuillInstance;
    register: (...args: unknown[]) => void;
    import: (path: string) => unknown;
    find?: (node: Node, bubble?: boolean) => unknown;
}

declare global {
    interface Window {
        Quill?: QuillStatic;
        QuillTableBetter?: unknown;
    }
}

interface QuillOptions {
    theme?: string;
    placeholder?: string;
    bounds?: HTMLElement | string | null;
    modules?: Record<string, unknown>;
    table?: boolean;
}

interface QuillRange {
    index: number;
    length: number;
}

interface QuillInstance {
    root: HTMLElement;
    getSelection: (focus?: boolean) => QuillRange | null;
    getLength: () => number;
    insertEmbed: (index: number, type: string, url: string, source?: string) => void;
    deleteText: (index: number, length: number, source?: string) => void;
    setSelection: (index: number, length?: number | string, source?: string) => void;
    getIndex?: (blot: unknown) => number;
    getModule: (name: string) => unknown;
    on: (event: string, fn: (...args: unknown[]) => void) => void;
    off: (event: string, fn: (...args: unknown[]) => void) => void;
    selection?: {
        normalizeNative: (native: Range | StaticRange) => unknown;
        normalizedToRange: (normalized: unknown) => QuillRange;
    };
}

export interface QuillEditorUploadConfig {
    uploadUrl: string;
    csrf: string;
    /** 폼 필드명: 'file' (comm) 또는 'image' (admin) */
    fileKey?: "file" | "image";
    /** 응답에서 URL 읽기: json.data.file_url 또는 json.data.url 등 */
    getUrl?: (json: Record<string, unknown>) => string | undefined;
}

export interface QuillEditorOptions {
    placeholder?: string;
    toolbar?: unknown[];
    upload?: QuillEditorUploadConfig;
    initialHtml?: string;
    /** 표 삽입 기능 (quill-table-better). 기본 false — 라이브러리가 로드돼 있어야 활성. */
    enableTable?: boolean;
    /** 동영상 업로드·드래그앤드롭 (mp4·webm·quicktime). 기본 false. */
    enableVideo?: boolean;
    /** 동영상 업로드 설정 — 생략 시 upload 와 같은 설정 사용 */
    videoUpload?: QuillEditorUploadConfig;
    /** 업로드 최대 파일 크기(byte). 기본 50MB. */
    maxFileBytes?: number;
}

const EDITOR_FONT_VALUES = richTextFontOptionsForQuill();
const DEFAULT_TOOLBAR_BASE = [
    [{ font: [false, ...EDITOR_FONT_VALUES] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] as unknown[] }, { background: [] as unknown[] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] as unknown[] }],
    ["blockquote", "code-block"],
    ["link", "image", "video"],
    ["clean"],
];

const TABLE_TOOLBAR_GROUP = ["table-better"];

const VIDEO_MIME_PREFIX = "video/";
const IMAGE_MIME_PREFIX = "image/";
const ALLOWED_IMAGE_MIMES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];
const ALLOWED_VIDEO_MIMES = ["video/mp4", "video/webm", "video/ogg", "video/quicktime"];
const DEFAULT_MAX_BYTES = 50 * 1024 * 1024; // 50MB
const INTERNAL_MEDIA_MIME = "application/x-admin-rich-editor-media";

/** 본문 HTML 에서 data: base64 이미지 제거 (요청 크기 방지) */
export function stripBase64Images(html: string): string {
    return (html || "").replace(/<img[^>]*src="data:image[^"]*"[^>]*>/gi, "");
}

function getUrlFromResponse(json: Record<string, unknown>): string | undefined {
    const data = json?.data as Record<string, unknown> | undefined;
    if (data?.file_url) return data.file_url as string;
    if (data?.url) return data.url as string;
    if (json?.url) return json.url as string;
    if (json?.location) return json.location as string;
    return undefined;
}

let tableBetterRegistered = false;
let fontWhitelistRegistered = false;

/**
 * Quill 기본 font whitelist 를 관리자 에디터용 공통 선택지로 확장한다.
 * 기본 sans(false) + RichTextOptions.ts 의 named font.
 */
function ensureFontWhitelistRegistered(): void {
    if (fontWhitelistRegistered) return;
    const Quill = window.Quill;
    if (!Quill) return;

    const Font = Quill.import("formats/font") as { whitelist?: string[] } | undefined;
    if (!Font) return;

    Font.whitelist = EDITOR_FONT_VALUES.slice();
    Quill.register(Font, true);
    fontWhitelistRegistered = true;
}

/**
 * quill-table-better 를 Quill 에 1회만 등록. window.QuillTableBetter 가 없으면 false 반환.
 */
function ensureTableBetterRegistered(): boolean {
    if (tableBetterRegistered) return true;
    const Quill = window.Quill;
    const TableBetter = window.QuillTableBetter;
    if (!Quill || !TableBetter) return false;
    try {
        Quill.register({ "modules/table-better": TableBetter }, true);
        tableBetterRegistered = true;
        return true;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[QuillEditor] quill-table-better 등록 실패:", err);
        return false;
    }
}

export class QuillEditor {
    private readonly container: HTMLElement;
    private instance: QuillInstance | null = null;
    private readonly opts: QuillEditorOptions;
    private dropIndicator: HTMLElement | null = null;
    private draggingMedia: { index: number; kind: "image" | "video"; value: string; element: HTMLElement } | null = null;

    constructor(container: string | HTMLElement, options: QuillEditorOptions = {}) {
        this.container =
            typeof container === "string"
                ? document.querySelector<HTMLElement>(container)!
                : container;
        this.opts = options;
    }

    init(): QuillInstance | null {
        const Quill = typeof window !== "undefined" ? window.Quill : undefined;
        if (!Quill || !this.container) return null;

        ensureFontWhitelistRegistered();
        const wantTable = !!this.opts.enableTable && ensureTableBetterRegistered();
        const toolbar = this.opts.toolbar ?? this.buildDefaultToolbar(wantTable);
        const upload = this.opts.upload?.uploadUrl ? this.opts.upload : undefined;
        const videoUpload = this.opts.enableVideo ? (this.opts.videoUpload ?? upload) : undefined;

        const modules: Record<string, unknown> = { toolbar };
        const uploader = this.buildUploaderModule(upload, videoUpload?.uploadUrl ? videoUpload : undefined);
        modules.uploader = uploader ?? false;
        if (wantTable) {
            // quill-table-better 권장 설정
            modules.table = false; // built-in table 비활성
            modules["table-better"] = {
                language: "en_US",
                menus: ["column", "row", "merge", "table", "cell", "wrap", "copy", "delete"],
                toolbarTable: true,
            };
            modules.keyboard = {
                bindings: (window.QuillTableBetter as { keyboardBindings?: unknown })?.keyboardBindings,
            };
        }

        this.instance = new Quill(this.container, {
            theme: "snow",
            placeholder: this.opts.placeholder ?? "",
            bounds: document.body,
            modules,
        });

        this.markEditorChrome();

        if (this.opts.initialHtml) {
            this.instance.root.innerHTML = this.opts.initialHtml;
        }

        if (upload?.uploadUrl && this.instance) {
            this.setupImageUpload(upload);
        }

        if (this.opts.enableVideo) {
            if (videoUpload?.uploadUrl) {
                this.setupVideoUpload(videoUpload);
            }
        }

        this.setupTooltipGuard();
        this.setupDragAndDropUi();
        this.setupMediaReorder();
        this.decorateMedia();
        this.instance.on("text-change", () => this.decorateMedia());

        return this.instance;
    }

    getInstance(): QuillInstance | null {
        return this.instance;
    }

    getHtml(): string {
        return this.instance?.root?.innerHTML ?? "";
    }

    setHtml(html: string): void {
        if (this.instance?.root) this.instance.root.innerHTML = html;
    }

    /** Quill text-change 이벤트 핸들러를 등록한다 (RichEditor 자동 sync 용). */
    onChange(fn: () => void): void {
        this.instance?.on("text-change", fn);
    }

    private buildDefaultToolbar(includeTable: boolean): unknown[] {
        const toolbar = DEFAULT_TOOLBAR_BASE.map((row) => row.slice());
        if (includeTable) {
            toolbar.push(TABLE_TOOLBAR_GROUP.slice());
        }
        return toolbar;
    }

    private markEditorChrome(): void {
        this.container.classList.add("admin-quill-container");
        const toolbar = this.container.previousElementSibling;
        if (toolbar instanceof HTMLElement && toolbar.classList.contains("ql-toolbar")) {
            toolbar.classList.add("admin-quill-toolbar");
        }
    }

    private buildUploaderModule(
        imageUpload?: QuillEditorUploadConfig,
        videoUpload?: QuillEditorUploadConfig,
    ): Record<string, unknown> | null {
        const mimetypes = [
            ...(imageUpload ? ALLOWED_IMAGE_MIMES : []),
            ...(videoUpload ? ALLOWED_VIDEO_MIMES : []),
        ];
        if (!mimetypes.length) return null;

        return {
            mimetypes,
            handler: (range: QuillRange, files: File[]) => {
                void this.uploadFilesAtRange(range, Array.from(files), imageUpload, videoUpload);
            },
        };
    }

    private uploadKindFor(file: File): "image" | "video" | null {
        const type = file.type ?? "";
        if (type.startsWith(IMAGE_MIME_PREFIX)) return "image";
        if (type.startsWith(VIDEO_MIME_PREFIX) && (ALLOWED_VIDEO_MIMES.includes(type) || type === VIDEO_MIME_PREFIX)) {
            return "video";
        }
        return null;
    }

    private isSupportedDragFile(file: File): boolean {
        const kind = this.uploadKindFor(file);
        if (kind === "image") return !!this.opts.upload?.uploadUrl;
        if (kind === "video") return !!(this.opts.enableVideo && (this.opts.videoUpload?.uploadUrl || this.opts.upload?.uploadUrl));
        return false;
    }

    private async uploadFilesAtRange(
        range: QuillRange,
        files: File[],
        imageUpload?: QuillEditorUploadConfig,
        videoUpload?: QuillEditorUploadConfig,
    ): Promise<void> {
        let index = range.index;
        let replaceLength = range.length ?? 0;

        for (const file of files) {
            const kind = this.uploadKindFor(file);
            const config = kind === "video" ? videoUpload : imageUpload;
            if (!kind || !config) continue;

            const inserted = await this.uploadAndInsertAt(file, kind, config, index, replaceLength);
            if (inserted) {
                index += 1;
                replaceLength = 0;
            }
        }
    }

    /** 공통 업로드 — 이미지·동영상 분기 처리. */
    private uploadAndInsert(file: File, kind: "image" | "video", config: QuillEditorUploadConfig): void {
        const quill = this.instance;
        if (!quill) return;

        const sel = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
        void this.uploadAndInsertAt(file, kind, config, sel.index, 0);
    }

    private uploadAndInsertAt(
        file: File,
        kind: "image" | "video",
        config: QuillEditorUploadConfig,
        index: number,
        replaceLength = 0,
    ): Promise<boolean> {
        const quill = this.instance;
        if (!quill) return Promise.resolve(false);

        const maxBytes = this.opts.maxFileBytes ?? DEFAULT_MAX_BYTES;
        if (file.size > maxBytes) {
            void Modal.alert(t("quill.file_too_big", { size: Math.round(file.size / 1024 / 1024), max: Math.round(maxBytes / 1024 / 1024) }));
            return Promise.resolve(false);
        }

        const fileKey = config.fileKey ?? "file";
        const getUrl = config.getUrl ?? getUrlFromResponse;

        const fd = new FormData();
        fd.append(fileKey, file);
        fd.append("_token", config.csrf);
        if (fileKey === "file") {
            fd.append("category", kind);
            fd.append("target_type", "post");
            fd.append("target_id", "0");
        }
        return fetch(config.uploadUrl, {
            method: "POST",
            body: fd,
            headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
        })
            .then((r) => r.json())
            .then((json: Record<string, unknown>) => {
                const url = getUrl(json);
                if (url) {
                    if (replaceLength > 0) {
                        quill.deleteText(index, replaceLength, "user");
                    }
                    quill.insertEmbed(index, kind, url, "user");
                    quill.setSelection(index + 1, 0, "silent");
                    this.decorateMedia();
                    return true;
                } else {
                    void Modal.alert(
                        (json.msg as string) ||
                            (json.message as string) ||
                            t(kind === "image" ? "quill.image_upload_failed" : "quill.video_upload_failed"),
                    );
                    return false;
                }
            })
            .catch(() => {
                void Modal.alert(t(kind === "image" ? "quill.image_upload_error" : "quill.video_upload_error"));
                return false;
            });
    }

    private setupImageUpload(config: QuillEditorUploadConfig): void {
        const quill = this.instance;
        if (!quill) return;

        // 툴바 image 버튼 → file picker
        const toolbar = quill.getModule("toolbar") as { addHandler?: (n: string, fn: () => void) => void } | null;
        if (toolbar?.addHandler) {
            toolbar.addHandler("image", () => this.openFilePicker("image/*", (file) => this.uploadAndInsert(file, "image", config)));
        }

        this.container.addEventListener("paste", (e: Event) => {
            const ev = e as ClipboardEvent;
            const items = ev.clipboardData?.items;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf("image/") === 0) {
                    ev.preventDefault();
                    const file = items[i].getAsFile();
                    if (file) this.uploadAndInsert(file, "image", config);
                    return;
                }
            }
        });
    }

    private setupVideoUpload(config: QuillEditorUploadConfig): void {
        const quill = this.instance;
        if (!quill) return;

        // 툴바 video 버튼 → file picker (없으면 Quill 기본 동작 유지)
        const toolbar = quill.getModule("toolbar") as { addHandler?: (n: string, fn: () => void) => void } | null;
        if (toolbar?.addHandler) {
            toolbar.addHandler("video", () => this.openFilePicker("video/*", (file) => this.uploadAndInsert(file, "video", config)));
        }

        // 동영상 paste
        this.container.addEventListener("paste", (e: Event) => {
            const ev = e as ClipboardEvent;
            const items = ev.clipboardData?.items;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
                const t = items[i].type;
                if (t.startsWith(VIDEO_MIME_PREFIX)) {
                    ev.preventDefault();
                    const file = items[i].getAsFile();
                    if (file) this.uploadAndInsert(file, "video", config);
                    return;
                }
            }
        });
    }

    private setupTooltipGuard(): void {
        const schedule = (): void => {
            window.requestAnimationFrame(() => this.keepTooltipInViewport());
        };

        this.container.addEventListener("click", schedule);
        this.container.addEventListener("keyup", schedule);
        this.instance?.on("selection-change", schedule);

        const scrollRoot = this.container.closest(".admin__main") ?? window;
        scrollRoot.addEventListener("scroll", schedule, { passive: true });
    }

    private keepTooltipInViewport(): void {
        const tooltip = this.container.querySelector<HTMLElement>(".ql-tooltip:not(.ql-hidden)");
        if (!tooltip) return;

        tooltip.classList.remove("ql-out-bottom", "ql-out-top");

        const margin = 12;
        const currentLeft = parseFloat(tooltip.style.left || "0") || 0;
        let nextLeft = currentLeft;
        let rect = tooltip.getBoundingClientRect();

        if (rect.left < margin) nextLeft += margin - rect.left;
        if (rect.right > window.innerWidth - margin) nextLeft -= rect.right - (window.innerWidth - margin);

        if (nextLeft !== currentLeft) {
            tooltip.style.left = `${Math.max(0, nextLeft)}px`;
        }

        const currentTop = parseFloat(tooltip.style.top || "0") || 0;
        let nextTop = currentTop;
        rect = tooltip.getBoundingClientRect();

        if (rect.bottom > window.innerHeight - margin) {
            nextTop -= rect.bottom - (window.innerHeight - margin) + margin;
            tooltip.classList.add("ql-flip");
        }

        rect = tooltip.getBoundingClientRect();
        if (rect.top < margin) {
            nextTop += margin - rect.top;
            tooltip.classList.remove("ql-flip");
        }

        if (nextTop !== currentTop) {
            tooltip.style.top = `${Math.max(0, nextTop)}px`;
        }
    }

    private setupDragAndDropUi(): void {
        const handleDragOver = (e: Event): void => {
            const ev = e as DragEvent;
            if (!this.dragHasSupportedData(ev)) return;

            ev.preventDefault();
            if (ev.dataTransfer) {
                ev.dataTransfer.dropEffect = this.draggingMedia ? "move" : "copy";
            }
            this.showDropIndicator(ev.clientY);
        };

        this.container.addEventListener("dragenter", handleDragOver, true);
        this.container.addEventListener("dragover", handleDragOver, true);
        this.container.addEventListener(
            "dragleave",
            (e: Event) => {
                const ev = e as DragEvent;
                const related = ev.relatedTarget;
                if (!(related instanceof Node) || !this.container.contains(related)) {
                    this.hideDropIndicator();
                }
            },
            true,
        );
        this.container.addEventListener(
            "drop",
            (e: Event) => {
                const ev = e as DragEvent;
                if (this.draggingMedia && this.dragHasSupportedData(ev)) {
                    this.dropDraggedMedia(ev);
                }
                this.hideDropIndicator();
            },
            true,
        );
    }

    private dragHasSupportedData(ev: DragEvent): boolean {
        if (this.draggingMedia) return true;

        const dt = ev.dataTransfer;
        if (!dt) return false;

        if (dt.items?.length) {
            for (let i = 0; i < dt.items.length; i++) {
                const item = dt.items[i];
                if (item.kind === "file") {
                    const type = item.type || "";
                    if (type.startsWith(IMAGE_MIME_PREFIX)) return !!this.opts.upload?.uploadUrl;
                    if (type.startsWith(VIDEO_MIME_PREFIX)) {
                        return !!(this.opts.enableVideo && (this.opts.videoUpload?.uploadUrl || this.opts.upload?.uploadUrl));
                    }
                }
            }
        }

        if (dt.files?.length) {
            for (let i = 0; i < dt.files.length; i++) {
                if (this.isSupportedDragFile(dt.files[i])) return true;
            }
        }

        if (Array.from(dt.types).includes("Files") && (this.opts.upload?.uploadUrl || this.opts.videoUpload?.uploadUrl)) {
            return true;
        }

        return false;
    }

    private ensureDropIndicator(): HTMLElement {
        if (!this.dropIndicator) {
            const indicator = document.createElement("div");
            indicator.className = "ql-drop-indicator";
            indicator.setAttribute("aria-hidden", "true");
            this.container.appendChild(indicator);
            this.dropIndicator = indicator;
        }
        return this.dropIndicator;
    }

    private showDropIndicator(clientY: number): void {
        const quill = this.instance;
        if (!quill) return;

        const indicator = this.ensureDropIndicator();
        const containerRect = this.container.getBoundingClientRect();
        const editorRect = quill.root.getBoundingClientRect();
        const top = Math.max(editorRect.top, Math.min(editorRect.bottom, clientY)) - containerRect.top;

        indicator.style.top = `${top}px`;
        indicator.style.left = `${editorRect.left - containerRect.left + 16}px`;
        indicator.style.width = `${Math.max(24, editorRect.width - 32)}px`;
        indicator.classList.add("is-visible");
        this.container.classList.add("is-file-dragging");
    }

    private hideDropIndicator(): void {
        this.dropIndicator?.classList.remove("is-visible");
        this.container.classList.remove("is-file-dragging");
    }

    private setupMediaReorder(): void {
        const quill = this.instance;
        if (!quill) return;

        quill.root.addEventListener(
            "dragstart",
            (e: Event) => {
                const ev = e as DragEvent;
                const target = this.getMediaTarget(ev.target);
                if (!target) return;

                const media = this.mediaDescriptorFor(target);
                if (!media) return;

                this.draggingMedia = media;
                media.element.classList.add("is-dragging");
                if (ev.dataTransfer) {
                    ev.dataTransfer.effectAllowed = "move";
                    ev.dataTransfer.setData(INTERNAL_MEDIA_MIME, JSON.stringify({ kind: media.kind, value: media.value }));
                    ev.dataTransfer.setData("text/plain", media.value);
                }
                ev.stopPropagation();
            },
            true,
        );

        quill.root.addEventListener("dragend", () => this.clearDraggingMedia(), true);
    }

    private decorateMedia(): void {
        const quill = this.instance;
        if (!quill) return;

        quill.root.querySelectorAll<HTMLElement>("img, video, iframe.ql-video").forEach((el) => {
            el.setAttribute("draggable", "true");
            el.classList.add("ql-media-draggable");
            if (!el.hasAttribute("tabindex")) {
                el.setAttribute("tabindex", "0");
            }
        });
    }

    private getMediaTarget(target: EventTarget | null): HTMLElement | null {
        if (!(target instanceof HTMLElement)) return null;
        return target.closest<HTMLElement>("img, video, iframe.ql-video");
    }

    private mediaDescriptorFor(element: HTMLElement): { index: number; kind: "image" | "video"; value: string; element: HTMLElement } | null {
        const quill = this.instance;
        if (!quill?.getIndex) return null;

        const blot = window.Quill?.find?.(element, true);
        if (!blot) return null;

        const index = quill.getIndex(blot);
        const kind = element instanceof HTMLImageElement ? "image" : "video";
        const value = element.getAttribute("src") || "";
        if (!value) return null;

        return { index, kind, value, element };
    }

    private dropDraggedMedia(ev: DragEvent): void {
        const quill = this.instance;
        const media = this.draggingMedia;
        if (!quill || !media) return;

        ev.preventDefault();
        ev.stopPropagation();

        const fallback = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1), length: 0 };
        const range = this.rangeFromPoint(ev.clientX, ev.clientY) || fallback;
        let targetIndex = Math.max(0, range.index);

        if (targetIndex === media.index || targetIndex === media.index + 1) {
            this.clearDraggingMedia();
            return;
        }

        quill.deleteText(media.index, 1, "user");
        if (media.index < targetIndex) {
            targetIndex = Math.max(0, targetIndex - 1);
        }
        targetIndex = Math.min(targetIndex, Math.max(0, quill.getLength() - 1));

        quill.insertEmbed(targetIndex, media.kind, media.value, "user");
        quill.setSelection(targetIndex + 1, 0, "silent");
        this.decorateMedia();
        this.clearDraggingMedia();
    }

    private clearDraggingMedia(): void {
        this.draggingMedia?.element.classList.remove("is-dragging");
        this.draggingMedia = null;
        this.hideDropIndicator();
    }

    private rangeFromPoint(clientX: number, clientY: number): QuillRange | null {
        const quill = this.instance;
        if (!quill?.selection) return null;

        const doc = document as Document & {
            caretRangeFromPoint?: (x: number, y: number) => Range | null;
            caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
        };

        let native: Range | null = null;
        if (doc.caretRangeFromPoint) {
            native = doc.caretRangeFromPoint(clientX, clientY);
        } else if (doc.caretPositionFromPoint) {
            const position = doc.caretPositionFromPoint(clientX, clientY);
            if (position) {
                native = document.createRange();
                native.setStart(position.offsetNode, position.offset);
                native.setEnd(position.offsetNode, position.offset);
            }
        }

        if (!native) return null;
        const normalized = quill.selection.normalizeNative(native);
        return normalized ? quill.selection.normalizedToRange(normalized) : null;
    }

    private openFilePicker(accept: string, onPicked: (file: File) => void): void {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = accept;
        input.style.display = "none";
        document.body.appendChild(input);
        input.addEventListener("change", () => {
            const file = input.files?.[0];
            input.value = "";
            if (file) onPicked(file);
            input.remove();
        });
        input.click();
    }
}
